import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import { getUserBadges, type Badge } from '@/services/authService';
import { toPersianNumber } from '@/utils/persianNumber';

const AchievementsPage = () => {
  const navigate = useNavigate();
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await getUserBadges();
        if (mounted) {
          // Log entire payload for debugging actual shape
          console.log('🏆 [Achievements] getUserBadges payload:', res);
          // Support both legacy and new shapes just in case
          const arr = Array.isArray((res as any)?.data?.badges)
            ? (res as any).data.badges
            : Array.isArray((res as any)?.badges)
            ? (res as any).badges
            : [];
          // Decode badge images before revealing the animated grid. Otherwise
          // the card animation finishes while the image is still blank.
          await Promise.all(arr.map((badge: Badge) => new Promise<void>((resolve) => {
            if (!badge.icon) {
              resolve();
              return;
            }
            const image = new Image();
            image.onload = () => resolve();
            image.onerror = () => resolve();
            image.src = badge.icon;
          })));
          if (mounted) setBadges(arr);
        }
      } catch (error) {
        console.error('Failed to load badges:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleBadgeClick = (badge: Badge) => {
    setSelectedBadge(badge);
  };

  const closeDialog = () => {
    setSelectedBadge(null);
  };

  const getIconDisplay = (badge: Badge, size: 'small' | 'large' = 'small') => {
    const sizeClass = size === 'small' ? 'w-24 h-24' : 'w-32 h-32';
    const iconSource = badge.icon;
    if (iconSource) {
      return (
        <img 
          src={iconSource}
          alt={badge.badge_name}
          className={cn(sizeClass, "object-contain")}
          loading="eager"
          decoding="sync"
          onError={(e) => {
            // Fallback to default icon if image fails to load
            (e.target as HTMLImageElement).style.display = 'none';
            (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
          }}
        />
      );
    }
    return null;
  };

  const getDefaultIcon = (size: 'small' | 'large' = 'small') => (
    <div className={size === 'small' ? 'text-6xl' : 'text-8xl'}>🏆</div>
  );

  return (
    <div className={cn('min-h-screen bg-white pb-20')} dir="rtl">
      {/* Header */}
      <div className="bg-[#ffffff] px-4 py-3 flex items-center justify-between sticky top-0 z-50 pt-safe-top">
        <div className="flex items-center justify-between w-full mt-2">
        <button 
          onClick={handleGoBack}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-all duration-200 hover:scale-110"
          aria-label="بازگشت"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path>
          </svg>
        </button>
        <h1 className="text-base font-bold text-gray-900">دستاوردها</h1>
        <div className="w-8"></div>
        </div>
      </div>

      <div
        className="container mx-auto px-4 py-6"
      >

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <motion.div 
              className="rounded-full h-12 w-12 border-b-2 border-primary"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-6 md:gap-8">
            {badges && badges.length > 0 ? badges.map((badge, index) => {
              const isUnlocked = badge.state !== 'locked';
              return (
                <div
                  key={badge.badge_id}
                  onClick={() => handleBadgeClick(badge)}
                  className={cn(
                    'achievement-icon-enter flex flex-col items-center cursor-pointer transition-transform duration-200 ease-out active:scale-95',
                    isUnlocked ? 'hover:scale-110' : 'hover:scale-105'
                  )}
                  style={{ animationDelay: `${index * 45}ms` }}
                >
                  {/* Icon Container with Glow Effect */}
                  <div 
                    className={cn(
                      'relative mb-1 flex justify-center items-center h-28 w-28 transition-[filter] duration-200',
                      isUnlocked && 'hover:drop-shadow-[0_0_8px_rgba(250,204,21,0.6)]',
                      !isUnlocked && 'opacity-40 filter grayscale'
                    )}
                  >
                    {getIconDisplay(badge)}
                    <div className={badge.icon ? 'hidden' : ''}>
                      {getDefaultIcon()}
                    </div>
                  </div>
                  
                  {/* Badge Name */}
                  <h3 className={cn(
                    'text-sm font-bold text-center font-persian leading-tight',
                    isUnlocked ? 'text-foreground' : 'text-gray-400'
                  )}>
                    {toPersianNumber(badge.badge_name)}
                  </h3>
                </div>
              );
            }) : (
              <div className="col-span-full flex justify-center items-center py-20">
                <p className="text-muted-foreground">هیچ دستاوردی یافت نشد</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Achievement Detail Dialog */}
      <AnimatePresence initial={false}>
        {selectedBadge && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onClick={closeDialog}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />
            
            {/* Dialog */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={closeDialog}
            >
              <motion.div
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl relative overflow-hidden"
              >
                {/* Decorative Background */}
                {selectedBadge.state !== 'locked' && (
                  <motion.div
                    className="absolute top-0 right-0 w-32 h-32 bg-yellow-400/10 rounded-full blur-3xl"
                    animate={{ 
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.5, 0.3]
                    }}
                    transition={{ 
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                )}

                {/* Close Button */}
                <button
                  onClick={closeDialog}
                  className="absolute top-4 left-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="بستن"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* Content */}
                <div className="flex flex-col items-center text-center relative z-10">
                  {/* Icon with Animation */}
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ 
                      type: "spring",
                      damping: 15,
                      stiffness: 200,
                      delay: 0.1
                    }}
                    className={cn(
                      'mb-3 flex justify-center items-center',
                      selectedBadge.state === 'locked' && 'opacity-40 filter grayscale'
                    )}
                  >
                    {selectedBadge.state !== 'locked' && (
                      <>
                        {/* Sparkle Effect */}
                        <motion.div
                          className="absolute"
                          animate={{ 
                            rotate: 360,
                          }}
                          transition={{ 
                            duration: 20,
                            repeat: Infinity,
                            ease: "linear"
                          }}
                        >
                          {[...Array(8)].map((_, i) => (
                            <motion.div
                              key={i}
                              className="absolute w-1 h-1 bg-yellow-400 rounded-full"
                              style={{
                                top: '50%',
                                left: '50%',
                                transform: `rotate(${i * 45}deg) translateY(-40px)`,
                              }}
                              animate={{
                                opacity: [0, 1, 0],
                                scale: [0, 1, 0],
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                delay: i * 0.2,
                              }}
                            />
                          ))}
                        </motion.div>
                      </>
                    )}
                    {getIconDisplay(selectedBadge, 'large')}
                    <div className={selectedBadge.icon ? 'hidden' : ''}>
                      {getDefaultIcon('large')}
                    </div>
                  </motion.div>

                  {/* Title */}
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-2xl font-bold text-gray-900 mb-2 font-persian"
                  >
                    {toPersianNumber(selectedBadge.badge_name)}
                  </motion.h2>

                  {/* Description */}
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-base text-gray-600 leading-relaxed font-persian"
                  >
                    {toPersianNumber(selectedBadge.description)}
                  </motion.p>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AchievementsPage;
